/**
 * Workspace tab vocabulary for the redesigned BDM/PO deals dashboard.
 *
 * Shared between the scorecard (DealDashboardMetrics — clicking a card
 * selects a tab), the route orchestrator (deals.tsx — holds activeTab
 * state), and the workspace grid (DealWorkspace — renders the tab bar and
 * filters rows). Defined once so the tab→status mapping and the
 * scorecard→tab routing can't drift.
 *
 * Status grouping per the dashboard plan (decision #5; CHP-797 moved an
 * extension awaiting review out of Live and into the In-review tab; CHP-976
 * split the old Archive/historical tab so declined registrations and lost
 * deals read separately):
 *   Live           = live
 *   In review      = pending_review + win_pending_review + extension_pending
 *   Closed Won     = closed_won
 *   Declined       = denied
 *   Closed-Lost    = closed_lost
 */

import type { DealDashboardMetrics, DealListItem, DealStatus } from "@/api/client";

export type DealTab = "active" | "pending" | "won" | "declined" | "lost";

export const DEAL_TAB_ORDER: readonly DealTab[] = ["active", "pending", "won", "declined", "lost"];

// CHP-364: labels renamed (My Active → Live, Closed-won); IDs stay stable to
// avoid route/test churn. CHP-976: Archive split into Declined + Closed-Lost
// (per-ticket labels; "Closed-Lost" matches DealStatusBadge's status copy).
export const DEAL_TAB_LABEL: Record<DealTab, string> = {
  active: "Live",
  pending: "In review",
  won: "Closed-won",
  declined: "Declined",
  lost: "Closed-Lost",
};

/** Statuses each tab includes. */
export const DEAL_TAB_STATUSES: Record<DealTab, readonly DealStatus[]> = {
  active: ["live"],
  pending: ["pending_review", "win_pending_review", "extension_pending"],
  won: ["closed_won"],
  declined: ["denied"],
  lost: ["closed_lost"],
};

export function dealMatchesTab(deal: Pick<DealListItem, "deal_status">, tab: DealTab): boolean {
  return DEAL_TAB_STATUSES[tab].includes(deal.deal_status);
}

/**
 * Tab bracket counts, sourced from the dashboard metrics (same query that
 * feeds the scorecard) rather than a separate request — see decision #5.
 * Each count matches its tab's status group exactly so the badge equals the
 * rendered row count.
 */
export function dealTabCount(metrics: DealDashboardMetrics, tab: DealTab): number {
  switch (tab) {
    case "active":
      return metrics.active_deals;
    case "pending":
      return metrics.pending_review + metrics.win_pending_review + metrics.extension_pending;
    case "won":
      return metrics.won_deals;
    case "declined":
      return metrics.denied_deals;
    case "lost":
      return metrics.lost_deals;
  }
}
